
import React from 'react';
import { Helmet } from 'react-helmet-async';
import SEOMetadata from '@/components/common/SEOMetadata';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { ExternalLink, BookOpen } from 'lucide-react';
import { Link } from 'react-router-dom';
import Hero from '@/components/Hero';

const PetitionJerryFox = () => {
  const scrollToSignSection = () => {
    const signSection = document.getElementById('sign-petition');
    if (signSection) {
      signSection.scrollIntoView({ behavior: 'smooth' });
    }
  };

  return (
    <div className="min-h-screen pt-20">
      <SEOMetadata
        title="Petition to Honor Jerry Fox"
        description="Sign the petition to honor Jerry Fox, Brooklyn's heroic blind cat, with a commemorative plaque at Borough Hall."
        canonicalUrl="https://catsabouttowntours.com/petition-jerry-fox"
        ogImage="/lovable-uploads/fb47f6db-abb0-4369-beee-a5478c1aa355.png"
        ogImageAlt="Historic photo of Jerry Fox"
        keywords={[
          'Jerry Fox',
          'Brooklyn cat hero',
          'Borough Hall plaque',
          'cat petition',
          'Brooklyn history'
        ]}
      />
      <Helmet>
        <script type="application/ld+json">
          {JSON.stringify({
            "@context": "https://schema.org",
            "@type": "BreadcrumbList",
            "itemListElement": [
              { "@type": "ListItem", "position": 1, "name": "Home", "item": "https://catsabouttowntours.com/" },
              { "@type": "ListItem", "position": 2, "name": "Petition for Jerry Fox", "item": "https://catsabouttowntours.com/petition-jerry-fox" }
            ]
          })}
        </script>
      </Helmet>

      {/* Hero Section */}
      <Hero 
        title="Commemorate Jerry Fox at Brooklyn Borough Hall" 
        subtitle="Sign the petition to honor Brooklyn's heroic cat with a permanent plaque"
        backgroundImage="/lovable-uploads/fb47f6db-abb0-4369-beee-a5478c1aa355.png"
        height="medium"
        imageStyle="darken"
        overlay={false}
        backgroundAlt="Historic photo of Jerry Fox"
      >
        <a
          href="https://docs.google.com/forms/d/1Q7iGAsbktYpAboxPOJq4GUhic8RE89UA-PagAFk1Lh8/viewform?edit_requested=true"
          target="_blank"
          rel="noopener noreferrer"
        >
          <Button 
            size="lg"
            className="mt-6"
          >
            Sign the Petition
          </Button>
        </a>
      </Hero>

      {/* Main Content Section */}
      <section className="py-16 bg-white">
        <div className="container mx-auto px-4 max-w-6xl">
          <div className="grid md:grid-cols-2 gap-12 items-start">
            {/* Left Column - Text Content */}
            <div className="space-y-6">
              <h2 className="text-3xl font-montserrat font-bold text-[#3E2C2F] mb-6">
                Why Jerry Fox Deserves to Be Remembered
              </h2>
              
              <div className="space-y-4 text-lg text-[#3E2C2F]/90 font-montserrat leading-relaxed">
                <p>
                  Born around 1876, Jerry Fox was a tiger-tabby cat who became Brooklyn's most beloved 
                  civic mascot. For nearly 28 years, he patrolled the area around Borough Hall and 
                  Terry Fox's café, known to judges, police, café-goers, trolley drivers, and locals alike.
                </p>
                
                <p>
                  Around 1903, Jerry lost his sight, but a local optometrist provided custom-made 
                  spectacles for him—an act covered by The New York Times, noting they gave him 
                  "a certain quaint dignity." Despite his blindness, he continued his daily rounds 
                  with confidence and grace.
                </p>
                
                <p>
                  On May 22, 1904, Jerry wandered into an unoccupied courtroom at Borough Hall where 
                  a smoldering cigar had ignited a fire threatening vital municipal records. His urgent 
                  meows and restless behavior drew officials to the scene, enabling them to extinguish 
                  the fire before it could spread further.
                </p>

                <p>
                  Jerry Fox represents resilience, loyalty, and community spirit—one of the few animals 
                  with documented civic heroism in Brooklyn history. We're calling on city officials 
                  to install a commemorative plaque at Borough Hall to honor his extraordinary legacy.
                </p>

                <div className="pt-4">
                  <Link to="/blog?post=jerry-fox-saves-brooklyn-borough-hall" className="inline-flex items-center text-[#C46829] hover:text-[#B35718] font-medium underline">
                    <BookOpen className="mr-2 h-4 w-4" />
                    Read the full story of Jerry Fox
                  </Link>
                </div>
              </div>
            </div>

            {/* Right Column - Image */}
            <div className="space-y-4">
              <div className="relative">
                <img
                  src="/lovable-uploads/15d7c76f-2960-4c03-b123-b6dec630aaa4.png"
                  alt="Jerry Fox, Brooklyn's heroic blind cat wearing spectacles"
                  className="w-full h-96 object-cover rounded-lg shadow-elegant"
                  loading="lazy"
                />
              </div>
              <p className="text-center text-sm text-[#3E2C2F]/70 font-montserrat italic">
                Jerry Fox, Brooklyn's heroic cat (1876–1904)
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Historical Context Section */}
      <section className="py-16 bg-[#FAF2E8]">
        <div className="container mx-auto px-4 max-w-4xl">
          <h2 className="text-3xl font-montserrat font-bold text-[#3E2C2F] mb-8 text-center">
            Jerry's Historical Significance
          </h2>
          
          <div className="grid md:grid-cols-2 gap-8 mb-12">
            <Card>
              <CardHeader>
                <CardTitle className="text-xl font-montserrat text-[#3E2C2F] flex items-center">
                  👓 Community Care & Resilience
                </CardTitle>
              </CardHeader>
              <CardContent>
                <p className="text-[#3E2C2F]/90 font-montserrat">
                  Jerry's custom spectacles represented unprecedented community care for an animal 
                  with disabilities in early 1900s Brooklyn, showing the deep bond between the 
                  neighborhood and their beloved mascot.
                </p>
              </CardContent>
            </Card>

            <Card>
              <CardHeader>
                <CardTitle className="text-xl font-montserrat text-[#3E2C2F] flex items-center">
                  🔥 Documented Heroism
                </CardTitle>
              </CardHeader>
              <CardContent>
                <p className="text-[#3E2C2F]/90 font-montserrat">
                  Jerry's fire detection at Borough Hall saved critical municipal documents and 
                  prevented potential destruction of this Greek Revival landmark, built in 1848 
                  as Brooklyn's original City Hall.
                </p>
              </CardContent>
            </Card>

            <Card>
              <CardHeader>
                <CardTitle className="text-xl font-montserrat text-[#3E2C2F] flex items-center">
                  📰 Media Recognition
                </CardTitle>
              </CardHeader>
              <CardContent>
                <p className="text-[#3E2C2F]/90 font-montserrat">
                  Jerry's story was covered by The New York Times and continues to inspire through 
                  modern media, blogs, and social platforms, keeping Brooklyn's animal history alive 
                  for new generations.
                </p>
              </CardContent>
            </Card>

            <Card>
              <CardHeader>
                <CardTitle className="text-xl font-montserrat text-[#3E2C2F] flex items-center">
                  🏛️ Brooklyn Legacy
                </CardTitle>
              </CardHeader>
              <CardContent>
                <p className="text-[#3E2C2F]/90 font-montserrat">
                  As Brooklyn's civic center evolved through the 1898 NYC consolidation, Jerry 
                  remained a constant, beloved presence, embodying the neighborhood's spirit and 
                  sense of community identity.
                </p>
              </CardContent>
            </Card>
          </div>
        </div>
      </section>

      {/* Call to Action Section */}
      <section id="sign-petition" className="py-16 bg-white">
        <div className="container mx-auto px-4 max-w-4xl text-center">
          <h2 className="text-3xl font-montserrat font-bold text-[#3E2C2F] mb-6">
            Help Honor Jerry Fox's Legacy
          </h2>
          <p className="text-xl text-[#3E2C2F]/80 font-montserrat mb-8 max-w-2xl mx-auto">
            Your signature helps us show Brooklyn Borough Hall officials how much Jerry's story 
            still matters to the community. Join hundreds of supporters calling for a commemorative 
            plaque to honor Brooklyn's most heroic cat.
          </p>
          
          <Card className="max-w-2xl mx-auto mb-8">
            <CardContent className="pt-8 pb-8">
              <h3 className="text-2xl font-montserrat font-bold text-[#3E2C2F] mb-4">
                Sign the Official Petition
              </h3>
              <p className="text-lg text-[#3E2C2F]/90 font-montserrat mb-6">
                Add your name to the growing list of supporters who believe Jerry Fox deserves 
                permanent recognition at Brooklyn Borough Hall.
              </p>
              
              <a
                href="https://docs.google.com/forms/d/1Q7iGAsbktYpAboxPOJq4GUhic8RE89UA-PagAFk1Lh8/viewform?edit_requested=true"
                target="_blank"
                rel="noopener noreferrer"
                className="inline-block"
              >
                <Button 
                  size="xl"
                  className="font-montserrat"
                >
                  <ExternalLink className="mr-2 h-5 w-5" />
                  Sign the Petition Now
                </Button>
              </a>
              
              <p className="text-sm text-[#3E2C2F]/70 font-montserrat mt-4">
                Opens in a new window • Powered by Google Forms
              </p>
            </CardContent>
          </Card>

          <div className="text-center">
            <p className="text-lg text-[#3E2C2F]/80 font-montserrat mb-4">
              Questions about the petition or Jerry's story?
            </p>
            <a 
              href="mailto:catsabouttowntour@gmail.com" 
              className="text-[#C46829] hover:text-[#B35718] font-montserrat font-medium underline"
            >
              Contact Cats About Town Tours
            </a>
          </div>
        </div>
      </section>
    </div>
  );
};

export default PetitionJerryFox;
